import { Col, Row, Space } from 'antd';
import { lowerCase } from 'lodash';
import moment from 'moment-timezone';
import React, { useMemo } from 'react';

import { IMAGES, INTERNAL_LINKS } from '../../../enum';
import { cleanQueryParams } from '../../../utils/queryParams';
import {
  sortColumnByDateString,
  sortColumnByMomentTime,
  sortColumnByStatus,
  sortColumnByUserFullName,
} from '../../../utils/tables';
import { CustomTable } from '../..';
import ProfileNamePicture from '../../shared-ui/ProfileNamePicture';
import Text from '../../Text';
import TextWithExternalLink from '../../TextWithExternalLink';

const TIME_FORMAT = 'hh:mm a';
const DATE_FORMAT = 'MM-DD-YYYY';

const TripsSubmissionHistoryTable = props => {
  const { t, rbId, rcId, dataSource = [] } = props;

  const COLUMNS = useMemo(
    () => [
      {
        width: 120,
        key: 'date',
        title: t('date'),
        ...sortColumnByDateString('timestamp'),
        render: (actions, data) => moment(data.timestamp, null).format(DATE_FORMAT),
      },
      {
        width: 120,
        key: 'time',
        title: t('time'),
        ...sortColumnByMomentTime('timestamp', TIME_FORMAT),
        render: (actions, data) => moment(data.timestamp, null).format(TIME_FORMAT),
      },
      {
        width: 200,
        key: 'user',
        title: t('user'),
        ...sortColumnByUserFullName('user'),
        render: (actions, data) => {
          if (typeof data.user === 'string') {
            return (
              <Text variant="strong" size="sm">
                {data.user}
              </Text>
            );
          }

          return <ProfileNamePicture t={t} user={data.user} />;
        },
      },
      {
        width: 340,
        key: 'reason',
        title: t('reason'),
        ...sortColumnByStatus('reason'),
        render: (actions, data) => {
          if (!data.reason) return null;
          const APPROVED_REASONS = ['semi-approval-rules'];
          const DENIED_REASONS = [
            'not-approved',
            'more-info-removal',
            'denied',
            'deleted',
            'denied-overlapping-leave-dates',
          ];
          const WAITING_REASONS = ['submitted'];

          return (
            <Row wrap={false} gutter={10} align="middle">
              <Col>
                {APPROVED_REASONS.includes(data.reason) && (
                  <img
                    width="24px"
                    height="24px"
                    src={IMAGES.CIRCLE_CHECK_MARK_ICON}
                    alt={data.reason}
                  />
                )}

                {DENIED_REASONS.includes(data.reason) && (
                  <img
                    width="24px"
                    height="24px"
                    src={IMAGES.CIRCLE_REMOVE_ICON}
                    alt={data.reason}
                  />
                )}

                {WAITING_REASONS.includes(data.reason) && (
                  <img width="24px" height="24px" src={IMAGES.ALERT_CLOCK} alt={data.reason} />
                )}
              </Col>
              <Col>
                <Space direction="vertical" size="small">
                  <Text size="sm">{t(data.reason)}</Text>
                  <Text size="sm">
                    {t('trips')}: {data.tripIds.length}{' '}
                    {!!data.tripIds.length && (
                      <TextWithExternalLink
                        text={lowerCase(t('viewTrips'))}
                        linkProps={{
                          size: 'xs',
                          to: `${INTERNAL_LINKS.SUBMISSION_HISTORY_TRIPS}?${cleanQueryParams({
                            rbId,
                            rcId,
                            tripIds: data.tripIds.join(','),
                          })}`,
                        }}
                      />
                    )}
                  </Text>
                </Space>
              </Col>
            </Row>
          );
        },
      },
    ],
    [t, rcId, rbId],
  );

  return (
    <CustomTable
      showSearchInput={false}
      columns={COLUMNS}
      dataSource={dataSource}
      scroll={{ x: 900, y: 400 }}
    />
  );
};

export default TripsSubmissionHistoryTable;
